import React from 'react';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Button from '@amzn/meridian/button';
import Icon from '@amzn/meridian/icon';
import closeSmallTokens from '@amzn/meridian-tokens/base/icon/close-small';
import exportSmallTokens from '@amzn/meridian-tokens/base/icon/export-small';
import infoTokens from '@amzn/meridian-tokens/base/icon/info';
import AssociateAvatar from '../../components/AssociateAvatar';
import { getAssociateDisplayLabel } from '../../utils';
import { AssignmentAssociate } from '../models';
import './AssociateCard.scss';

export interface AssociateCardProps {
  /** The associate to display. */
  associate: AssignmentAssociate;
  /**
   * Optional assign action. When provided, an "Assign" button is rendered at
   * the trailing edge of the card and invokes this callback with the
   * associate's id. When omitted, no button is shown — so the card stays a
   * plain read-only row until the assignment flow is wired up.
   */
  onAssign?: (associateId: string) => void;
  /**
   * When true, show the associate's per-station id (`assignedWorkstationId`)
   * in place of their name — mirroring the WorkstationCard "Show WS IDs" view
   * for pool-rendered functions. Falls back to the name when the associate has
   * no station id (e.g. the unassigned sidebar or non-workstation associates).
   */
  showWorkstationId?: boolean;
  /**
   * Optional unassign action. When provided, a trailing unassign (✕) icon
   * button is rendered and invokes this callback with the associate's id. Used
   * for pool (department/indirect) associates so they can be returned to the
   * unassigned pool. When omitted, no unassign button is shown.
   */
  onUnassign?: (associateId: string) => void;
  /**
   * Optional move action. When provided, a move (↗) icon button is rendered
   * just before the unassign (✕) button and invokes this callback with the
   * associate's id. Used for pool (department/indirect) associates so they can
   * be moved to a new location. When omitted, no move button is shown.
   */
  onMove?: (associateId: string) => void;
  /**
   * Optional "view decision" action. When provided, an info (ℹ️) icon button
   * is rendered as the leftmost action and invokes this callback with the
   * associate's id. Used for pool associates so a planner can inspect why they
   * are assigned. When omitted, no info button is shown.
   */
  onViewDecision?: (associateId: string) => void;
}

/**
 * A single associate row for the redesigned Station Assignment screen.
 *
 * Renders only what the current API reliably provides: the associate's
 * avatar (from `associateAlias`) and display name. The optional trailing
 * "Assign" button appears solely when the parent supplies `onAssign`.
 *
 * Composed from Meridian primitives: a Row centers the avatar, name and
 * optional action; the name fills the remaining width (see SCSS) so it
 * truncates with an ellipsis and any trailing button aligns to the edge.
 */
const AssociateCard: React.FC<AssociateCardProps> = ({
  associate,
  onAssign,
  showWorkstationId = false,
  onUnassign,
  onMove,
  onViewDecision,
}) => (
  <Row
    className="assignments-associate-card"
    alignmentVertical="center"
    spacing="200"
    data-testid={`associate-card-${associate.associateId}`}
  >
    <AssociateAvatar
      associateName={associate.associateName}
      associateAlias={associate.associateAlias}
      className="assignments-associate-card__avatar"
    />
    <Text type="b300" className="assignments-associate-card__name">
      {showWorkstationId && associate.assignedWorkstationId
        ? associate.assignedWorkstationId
        : getAssociateDisplayLabel(associate.associateAlias, associate.associateName)}
    </Text>
    {onAssign && associate.associateId && (
      <Button
        type="tertiary"
        size="small"
        onClick={() => onAssign(associate.associateId as string)}
        data-testid={`associate-card-assign-${associate.associateId}`}
      >
        Assign
      </Button>
    )}
    {associate.associateId && (onViewDecision || onMove || onUnassign) && (
      <Row
        className="assignments-associate-card__actions"
        spacing="100"
        alignmentVertical="center"
        spacingInset="none"
        width="fit"
      >
        {onViewDecision && associate.associateId && (
          <Button
            type="icon"
            size="small"
            label={`View decision for ${associate.associateName}`}
            onClick={() => onViewDecision(associate.associateId as string)}
            data-testid={`associate-card-info-${associate.associateId}`}
          >
            <Icon tokens={infoTokens}>Info</Icon>
          </Button>
        )}
        {onMove && associate.associateId && (
          <Button
            type="icon"
            size="small"
            label={`Move ${associate.associateName}`}
            onClick={() => onMove(associate.associateId as string)}
            data-testid={`associate-card-move-${associate.associateId}`}
          >
            <Icon tokens={exportSmallTokens}>Move</Icon>
          </Button>
        )}
        {onUnassign && associate.associateId && (
          <Button
            type="icon"
            size="small"
            label={`Unassign ${associate.associateName}`}
            onClick={() => onUnassign(associate.associateId as string)}
            data-testid={`associate-card-unassign-${associate.associateId}`}
          >
            <Icon tokens={closeSmallTokens}>Unassign</Icon>
          </Button>
        )}
      </Row>
    )}
  </Row>
);

export default AssociateCard;

import './AssociateCard.scss';